/**
 * "Paradox" — original mirror/dual/teleport level (difficulty 6/10).
 * The view flips, the body splits, and a teleport gate skips you onto a
 * high road — then a 2× run home. Authored in block coordinates → SSL.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0, hanging = false): void =>
  push({ type: 'spike', ...cell(bx, by), ...(hanging ? { flipY: true } : {}) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};
const portal = (bx: number, kind: string, by = 1): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind } });

// --- Straight opening (0–36) -----------------------------------------------------
spike(22);
spike(23);
spike(30);
spike(31);

// --- Mirrored view (36–60) --------------------------------------------------------
portal(38, 'mirror:on');
spike(46);
spike(47);
spike(54);
spike(55);

// --- Dual corridor (60–102): both bodies dodge symmetric spikes -------------------
portal(62, 'dual:on');
ceiling(64, 98, 6);
spike(70);
spike(71);
spike(70, 5, true);
spike(71, 5, true);
spike(80);
spike(81);
spike(80, 5, true);
spike(81, 5, true);
spike(90);
spike(91);
spike(90, 5, true);
spike(91, 5, true);
portal(100, 'dual:off');
portal(102, 'mirror:off');

// --- Teleport high road (106–126) --------------------------------------------------
push({ type: 'portal', ...cell(108, 1), props: { kind: 'teleport', dx: 0, dy: 4 } });
for (let bx = 109; bx <= 117; bx++) block(bx, 3);
spike(113, 4); // spike on the high road
// (drop back to ground after 117)

// --- 2× run home (122–154) ----------------------------------------------------------
portal(124, 'speed:two');
spike(132);
spike(133);
spike(142);
spike(143);

// Secret shards.
push({ type: 'collectible', ...cell(35, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(85, 3), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(115, 5), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(154, 2) });

export const PARADOX: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Paradox',
    author: 'Shape Shifters Team',
    description: 'Two of you. One mistake allowed. Zero, actually.',
    revision: 1,
    createdAt: '2026-07-24T00:00:00.000Z',
    updatedAt: '2026-07-24T00:00:00.000Z',
    difficulty: 6,
    tags: ['dual', 'mirror', 'official'],
  },
  song: { id: 'circuit-breaker', bpm: 140, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#e879f9',
    hazardColor: '#f43f5e',
    backgroundColor: '#16041c',
  },
  objects,
};
